import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  ChevronLeft, 
  ChevronRight,
  Target,
  Send,
  LogOut,
  BarChart3,
  Settings,
  MessageSquareMore,
  User as UserIcon,
  ShieldCheck,
  UserCog
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useUserProfileContext } from '../context/UserProfileContext';
import { useFeatures } from '../hooks/useFeatures';
import { useIsAdmin } from '../hooks/useIsAdmin';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavItem {
  path: string;
  icon: React.ReactNode;
  label: string;
  protected?: boolean;
  requiresDirectReports?: boolean;
  requiresAdmin?: boolean;
}

export function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const location = useLocation();
  const { isAuthenticated, logout, userInfo } = useAuth();
  const { userProfile } = useUserProfileContext();
  const { userCvUi } = useFeatures();
  const { isAdmin } = useIsAdmin();
  const [isMobile, setIsMobile] = useState(false);

  const hasDirectReports = useMemo(() => 
    (userProfile?.directReports?.length || 0) > 0,
    [userProfile?.directReports?.length]
  );

  // A Pseudo Manager must not see the Pseudo Manager nav item (no chaining, no confusion with their own status).
  const isPseudoManager = userProfile?.isPseudoManager ?? false;

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Navigation items - protected routes only visible to authenticated users
  const navItems: NavItem[] = [
    {
      path: '/dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      label: 'Dashboard',
      protected: true
    },
    {
      path: '/team',
      icon: <Users className="w-5 h-5" />,
      label: 'Team',
      protected: true
    },
    {
      path: '/analytics',
      icon: <BarChart3 className="w-5 h-5" />,
      label: 'Analytics',
      protected: true,
      requiresDirectReports: true
    },
    {
      path: '/manage-questions',
      icon: <Settings className="w-5 h-5" />,
      label: 'Manage Questions',
      protected: true,
      requiresDirectReports: true
    },
    {
      path: '/manage-core-priorities',
      icon: <Target className="w-5 h-5" />,
      label: 'Core Priorities',
      protected: true,
      requiresDirectReports: true
    },
    {
      path: '/pseudo-managers',
      icon: <UserCog className="w-5 h-5" />,
      label: 'Pseudo Manager',
      protected: true,
      requiresDirectReports: true
    },
    {
      path: '/feedback',
      icon: <MessageSquareMore className="w-5 h-5" />,
      label: 'Feedback',
      protected: true
    },
    {
      path: '/profile',
      icon: <UserIcon className="w-5 h-5" />,
      label: 'Profile',
      protected: true
    },
    {
      path: '/admin',
      icon: <ShieldCheck className="w-5 h-5" />,
      label: 'Admin',
      protected: true,
      requiresAdmin: true
    }
  ];

  // Filter items based on authentication, manager status, and feature flags
  const visibleItems = navItems.filter(item => {
    if (item.protected && !isAuthenticated) {
      return false;
    }
    if (item.requiresDirectReports && !hasDirectReports) {
      return false;
    }
    if (item.path === '/pseudo-managers' && isPseudoManager) {
      return false;
    }
    if (item.requiresAdmin && !isAdmin) {
      return false;
    }
    // Hide the Profile/CV entry when the UserCvUi feature flag is off
    // (configured via Features__UserCvUi App Service application setting in prod).
    if (item.path === '/profile' && !userCvUi) {
      return false;
    }
    return true;
  });

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Don't render sidebar if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-16 left-0 h-[calc(100dvh-4rem)] z-40
          bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900
          border-r border-slate-700/50
          shadow-xl shadow-slate-900/50
          transition-all duration-300 ease-in-out
          ${isOpen ? 'w-64' : 'w-16'}
          ${isMobile ? (isOpen ? 'w-[min(20rem,calc(100vw-3rem))] translate-x-0' : '-translate-x-full') : 'translate-x-0'}
        `}
      >
        {/* Toggle Button - Desktop */}
        <button
          onClick={onToggle}
          className="hidden lg:flex absolute -right-3 top-6 w-6 h-6 bg-pink-500 rounded-full items-center justify-center text-white hover:bg-pink-600 transition-colors shadow-lg"
        >
          {isOpen ? (
            <ChevronLeft className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {/* Navigation Links */}
        <nav className="h-full overflow-y-auto p-3 pt-5 pb-40 space-y-1">
          {visibleItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => isMobile && onToggle()}
              className={`
                flex items-center rounded-xl
                transition-all duration-200
                group relative
                ${isOpen ? 'px-3 py-3 gap-3' : 'px-0 py-3 justify-center'}
                ${isActive(item.path)
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-pink-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }
              `}
            >
              <span className={`
                flex-shrink-0 transition-all duration-200
                ${isOpen ? '' : 'mx-auto'}
                ${isActive(item.path) ? 'text-white' : 'text-slate-400 group-hover:text-pink-400'}
              `}>
                {item.icon}
              </span>
              
              {/* Label - visible when expanded */}
              {isOpen && (
                <span className="font-medium whitespace-nowrap">
                  {item.label}
                </span>
              )}

              {/* Tooltip - visible when collapsed */}
              {!isOpen && (
                <div className="
                  absolute left-full ml-3 px-3 py-2 
                  bg-slate-800 text-white text-sm font-medium
                  rounded-lg shadow-lg
                  opacity-0 invisible group-hover:opacity-100 group-hover:visible
                  transition-all duration-200
                  whitespace-nowrap
                  z-50
                  border border-slate-700
                ">
                  {item.label}
                  <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-2 h-2 bg-slate-800 border-l border-b border-slate-700 rotate-45" />
                </div>
              )}
            </Link>
          ))}
        </nav>

        {/* Sign Out Button */}
        <div className={`
          absolute bottom-4 left-0 right-0 px-3
          transition-all duration-300
        `}>
          <div className="border-t border-slate-700/50 pt-4">
            {isMobile && userInfo && (
              <>
                <a
                  href="https://forms.office.com/r/RdVxcG394Y"
                  target="_blank"
                  rel="noreferrer"
                  className="mb-2 flex items-center gap-3 rounded-xl px-3 py-3 text-slate-300 transition-colors hover:bg-slate-700/50 hover:text-white"
                >
                  <Send className="h-5 w-5 text-pink-400" />
                  <span className="font-medium">Send Feedback</span>
                </a>
                <div className="mb-2 flex items-center gap-3 px-3 py-2 text-white">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-pink-500">
                    {userInfo.photoUrl ? (
                      <img src={userInfo.photoUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <UserIcon className="h-5 w-5" />
                    )}
                  </div>
                  <span className="truncate font-medium">{userInfo.displayName || userInfo.name || 'User'}</span>
                </div>
              </>
            )}
            <button
              onClick={logout}
              className={`
                flex items-center w-full rounded-xl
                transition-all duration-200
                text-slate-400 hover:text-white hover:bg-red-500/20
                group relative
                ${isOpen ? 'px-3 py-3 gap-3' : 'px-0 py-3 justify-center'}
              `}
            >
              <LogOut className={`w-5 h-5 flex-shrink-0 group-hover:text-red-400 ${isOpen ? '' : 'mx-auto'}`} />
              {isOpen && (
                <span className="font-medium whitespace-nowrap">Sign Out</span>
              )}
              {/* Tooltip - visible when collapsed */}
              {!isOpen && (
                <div className="
                  absolute left-full ml-3 px-3 py-2 
                  bg-slate-800 text-white text-sm font-medium
                  rounded-lg shadow-lg
                  opacity-0 invisible group-hover:opacity-100 group-hover:visible
                  transition-all duration-200
                  whitespace-nowrap
                  z-50
                  border border-slate-700
                ">
                  Sign Out
                  <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-2 h-2 bg-slate-800 border-l border-b border-slate-700 rotate-45" />
                </div>
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
